import { useMemo } from 'react';
import { useSubscription } from 'urql';
import { CallStatsByStatusSubscription, CallStatsTotalsSubscription } from './call-graphql';

function useStatusCount(organizationId: string | null, startedAt: string, status: string) {
  const [{ data }] = useSubscription({
    query: CallStatsByStatusSubscription,
    variables: { organizationId: organizationId ?? '', startedAt, status },
    pause: !organizationId,
  });

  return data ? (data.callLogsAggregate.aggregate?.count ?? 0) : undefined;
}

export function useCallStats(organizationId: string | null, startedAt: string) {
  const [{ data: totals }] = useSubscription({
    query: CallStatsTotalsSubscription,
    variables: { organizationId: organizationId ?? '', startedAt },
    pause: !organizationId,
  });

  const completedCount = useStatusCount(organizationId, startedAt, 'completed');
  const reapedCount = useStatusCount(organizationId, startedAt, 'reaped');
  const failedCount = useStatusCount(organizationId, startedAt, 'failed');
  const noAnswerCount = useStatusCount(organizationId, startedAt, 'no_answer');
  const busyCount = useStatusCount(organizationId, startedAt, 'busy');
  const transferredCount = useStatusCount(organizationId, startedAt, 'transferred');

  return useMemo(() => {
    if (
      !totals ||
      completedCount === undefined ||
      reapedCount === undefined ||
      failedCount === undefined ||
      noAnswerCount === undefined ||
      busyCount === undefined ||
      transferredCount === undefined
    ) {
      return null;
    }

    return {
      totalCalls: totals.callLogsAggregate.aggregate?.count ?? 0,
      completedCalls: completedCount + reapedCount,
      failedCalls: failedCount,
      totalDurationSeconds: totals.callLogsAggregate.aggregate?.sum?.durationSeconds ?? 0,
      averageDurationSeconds: Math.round(totals.callLogsAggregate.aggregate?.avg?.durationSeconds ?? 0),
      byStatus: [
        { status: 'completed', count: completedCount },
        { status: 'failed', count: failedCount },
        { status: 'no_answer', count: noAnswerCount },
        { status: 'busy', count: busyCount },
        { status: 'transferred', count: transferredCount },
        { status: 'reaped', count: reapedCount },
      ].filter((entry) => entry.count > 0),
    };
  }, [totals, completedCount, reapedCount, failedCount, noAnswerCount, busyCount, transferredCount]);
}
